import React, { useRef } from "react";
export const FRAME = {
  width: 471.42,
  height: 360,
  sourceLeft: 74.88,
  sourceTop: 217.08,
  photoLeft: 254.12,
  photoTop: 77.92,
};
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
export function fixedCrop(g, zoom, left, top) {
  const width = FRAME.width / (g.source_width * zoom),
    height = FRAME.height / (g.source_height * zoom);
  return {
    x: clamp(left / g.source_width, 0, 1 - width),
    y: clamp(top / g.source_height, 0, 1 - height),
    width,
    height,
  };
}
export default function FixedLicenseFrame({
  source,
  geometry: g,
  crop,
  onChange,
  photo,
  photoCrop,
}) {
  const stage = useRef(null),
    drag = useRef(null);
  const minZoom = g
    ? Math.max(FRAME.width / g.source_width, FRAME.height / g.source_height)
    : 1;
  const reset = () => {
    if (g)
      onChange(
        fixedCrop(g, Math.max(1, minZoom), FRAME.sourceLeft, FRAME.sourceTop),
      );
  };
  if (!source || !g) return <p role="status">Opening license…</p>;
  const zoom = FRAME.width / (crop.width * g.source_width);
  const changeZoom = (value) => {
    const x = crop.x * g.source_width + FRAME.width / (2 * zoom),
      y = crop.y * g.source_height + FRAME.height / (2 * zoom);
    onChange(
      fixedCrop(
        g,
        value,
        x - FRAME.width / (2 * value),
        y - FRAME.height / (2 * value),
      ),
    );
  };
  return (
    <div className="simple-fixed">
      <div className="simple-fixed__top">
        <label>Zoom<input aria-label="Document alignment zoom" type="range" min={minZoom} max={Math.max(4,minZoom)} step=".01" value={zoom} onChange={e=>changeZoom(Number(e.target.value))}/></label>
        <button type="button" className="simple-license__text" onClick={reset}>
          Reset view
        </button>
      </div>
      <div className="simple-fixed__well">
        <div
          ref={stage}
          className="simple-fixed__aperture"
          role="group"
          aria-label="Fixed license frame; drag document to align, arrow keys to move"
          tabIndex={0}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            stage.current.focus();
            stage.current.setPointerCapture?.(e.pointerId);
            drag.current = {
              id: e.pointerId,
              x: e.clientX,
              y: e.clientY,
              crop,
            };
          }}
          onPointerMove={(e) => {
            const start = drag.current;
            if (!start || start.id !== e.pointerId) return;
            const rect = stage.current.getBoundingClientRect();
            onChange(
              fixedCrop(
                g,
                zoom,
                start.crop.x * g.source_width -
                  ((e.clientX - start.x) * FRAME.width) / rect.width / zoom,
                start.crop.y * g.source_height -
                  ((e.clientY - start.y) * FRAME.height) / rect.height / zoom,
              ),
            );
          }}
          onPointerUp={(e) => {
            drag.current = null;
            stage.current.releasePointerCapture?.(e.pointerId);
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
          onKeyDown={(e) => {
            if (
              !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                e.key,
              )
            )
              return;
            e.preventDefault();
            const v = (e.shiftKey ? 20 : 5) / zoom;
            onChange(
              fixedCrop(
                g,
                zoom,
                crop.x * g.source_width +
                  (e.key === "ArrowLeft" ? v : e.key === "ArrowRight" ? -v : 0),
                crop.y * g.source_height +
                  (e.key === "ArrowUp" ? v : e.key === "ArrowDown" ? -v : 0),
              ),
            );
          }}
        >
          <img
            src={source}
            alt="License document behind fixed-size print frame"
            draggable={false}
            style={{
              width: `${((g.source_width * zoom) / FRAME.width) * 100}%`,
              height: `${((g.source_height * zoom) / FRAME.height) * 100}%`,
              left: `${((-crop.x * g.source_width * zoom) / FRAME.width) * 100}%`,
              top: `${((-crop.y * g.source_height * zoom) / FRAME.height) * 100}%`,
            }}
          />
          {photo && photoCrop && <span className="simple-fixed__profile" style={{left:`${FRAME.photoLeft/FRAME.width*100}%`,top:`${FRAME.photoTop/FRAME.height*100}%`,width:`${144/FRAME.width*100}%`,height:`${144/FRAME.height*100}%`}}><img src={photo} alt="Existing profile photo, 2 by 2 inches in print" draggable={false} style={{width:`${100/photoCrop.width}%`,height:`${100/photoCrop.height}%`,left:`${-photoCrop.x/photoCrop.width*100}%`,top:`${-photoCrop.y/photoCrop.height*100}%`}}/></span>}
          <span className="simple-fixed__corner simple-fixed__corner--tl" />
          <span className="simple-fixed__corner simple-fixed__corner--tr" />
          <span className="simple-fixed__corner simple-fixed__corner--bl" />
          <span className="simple-fixed__corner simple-fixed__corner--br" />
        </div>
      </div>
    </div>
  );
}
